// import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Check, Plus, ArrowUpRight } from "lucide-react";
// import { Box, Typography, Stack } from "@mui/material";
import { PRODUCTS, getProduct } from "../data/products";
import { useSEO } from "../lib/seo";
import { Reveal } from "../site/Reveal";
import { CTASection } from "../site/CTASection";
import FAQs from "../components/FAQs";
import SpecTable from "../components/SpecTable";
import { useParams, Link as RouterLink, Navigate } from "react-router-dom";
import { Box, Container, Typography, Grid, Link, Stack } from "@mui/material";
import ProductFAQ from "../components/ProductFAQ";
const COLORS = {
  obsidian: "#121212",
  safety: "#ff6b00", // Standard safety orange/yellow accent
  concrete: "#f5f5f5", // Light gray background
  ink: "#1a1a1a", // Deep dark text
  steel: "#708090",
  muted: "#666666",
  borderLight: "rgba(26, 26, 26, 0.1)", // border-ink/10
  borderMedium: "rgba(26, 26, 26, 0.15)", // border-ink/15
};

export default function ProductDetail() {
  const { slug } = useParams();
  const product = getProduct(slug);

  useSEO({
    title: product
      ? `${product.short} Manufacturer | ${product.name} | ITL Technical Services`
      : "Product | ITL Technical Services",
    description: product ? product.description.slice(0, 155) : "",
    jsonLd: product && {
      "@context": "https://schema.org",
      "@type": "Product",
      name: product.name,
      description: product.description,
      image: product.image,
      brand: { "@type": "Brand", name: "ITL Technical Services" },
      manufacturer: { "@type": "Organization", name: "ITL Technical Services" },
    },
  });

  if (!product) return <Navigate to="/products" replace />;

  const others = PRODUCTS.filter((p) => p.slug !== slug);

  return (
    <>
      {/* HERO */}
      <Box
        component="section"
        data-testid="product-hero"
        className="grain grain-dark"
        sx={{
          position: "relative",
          color: "#fff",
          overflow: "hidden",
          pt: { xs: 16, md: 22 },
          pb: { xs: 8, md: 12 },

          backgroundImage: `
  url("${product.banner}")
`,
          filter: "grayscale(100%)",

          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        {/* Background Image & Overlay */}
        <Box sx={{ position: "absolute", inset: 0, zIndex: 0 }}>
          <Box
            component="img"
            sx={{
              width: "1/1",
              height: "1/1",
              objectFit: "cover",
              opacity: 0.25,
              filter: "grayscale(100%)",
            }}
          />
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background: `linear-gradient(to top, ${COLORS.obsidian}, rgba(18, 18, 18, 0.8), rgba(18, 18, 18, 0.6))`,
            }}
          />
        </Box>

        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 10 }}>
          <Typography
            component="span"
            sx={{
              display: "block",
              fontFamily: "monospace",
              color: COLORS.safety,
              textAlign: "left",
            }}
          >
            [{product.index}]
          </Typography>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          >
            <Typography
              component="h1"
              sx={{
                fontFamily: "Cabinet Grotesk, system-ui, sans-serif",
                fontWeight: 900,
                fontSize: { xs: "2.25rem", md: "3.75rem", lg: "4.5rem" },
                tracking: "-0.05em",
                lineHeight: 0.9,
                mt: 2,
                maxWidth: "56rem", // max-w-4xl
                textAlign: "left",
              }}
            >
              {product.name}
            </Typography>
          </motion.div>

          <Typography
            sx={{
              fontFamily: "monospace",
              color: "rgba(255, 255, 255, 0.6)",
              mt: 3,
              fontSize: { xs: "0.875rem", md: "1rem" },
              textAlign: "left",
            }}
          >
            {product.tagline}
          </Typography>
        </Container>
      </Box>

      {/* DESCRIPTION */}
      <Box component="section" sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Reveal>
            <Grid container spacing={4}>
              <Grid item xs={12} md={3}>
                <Typography
                  component="h2"
                  sx={{
                    fontFamily: "monospace",
                    fontSize: "0.75rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.1em",
                    color: COLORS.safety,
                  }}
                >
                  [ Overview ]
                </Typography>
              </Grid>
              <Grid item xs={12} md={9}>
                <Typography
                  sx={{
                    color: COLORS.ink,
                    fontSize: { xs: "1.25rem", md: "1.5rem" },
                    fontFamily: "display",
                    lineHeight: 1.3,
                    letterSpacing: "-0.02em",
                    textAlign: "left",
                  }}
                >
                  {product.description}
                </Typography>
              </Grid>
            </Grid>
          </Reveal>
        </Container>
      </Box>

      {/* SPEC TABLE */}
      {product.specs.length > 0 && (
        <Box
          component="section"
          data-testid="specs-section"
          sx={{
            backgroundColor: COLORS.concrete,
            borderTop: `1px solid ${COLORS.borderLight}`,
            borderBottom: `1px solid ${COLORS.borderLight}`,
            py: { xs: 8, md: 12 },
          }}
        >
          <Container maxWidth="lg">
            <Reveal>
              <Typography
                component="h2"
                sx={{
                  fontFamily: "display",
                  fontWeight: 900,
                  fontSize: { xs: "1.875rem", md: "2.25rem" },
                  letterSpacing: "-0.03em",
                  color: COLORS.ink,
                  mb: 5,
                  fontFamily: "Cabinet Grotesk, system-ui, sans-serif",
                  textAlign: "left",
                }}
              >
                General Specifications
              </Typography>
            </Reveal>
            <Reveal delay={0.1}>
              <SpecTable
                specs={product.specs}
                caption={`${product.name} specifications`}
              />
            </Reveal>
          </Container>
        </Box>
      )}

      {/* FEATURES */}
      <Box component="section" sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: product.optional.length > 0 ? "1fr 1fr" : "1fr",
              },
              gap: { xs: 6, lg: 10 },
            }}
          >
            {/* Salient Features */}
            <Reveal>
              <Box>
                <Typography
                  component="h2"
                  sx={{
                    fontFamily: "Cabinet Grotesk, system-ui, sans-serif",
                    fontWeight: 900,
                    fontSize: { xs: "1.875rem", md: "2.25rem" },
                    letterSpacing: "-0.03em",
                    color: COLORS.ink,
                    mb: 4,
                    textAlign: "left",
                  }}
                >
                  Salient Features
                </Typography>

                <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0 }}>
                  {product.features.map((f, i) => (
                    <Box
                      component="li"
                      key={i}
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: 2,
                        borderBottom: `1px solid ${COLORS.borderLight}`,
                        pb: 2,
                        mb: 2,
                        "&:last-child": {
                          mb: 0,
                        },
                      }}
                    >
                      <Check
                        style={{
                          width: 20,
                          height: 20,
                          color: COLORS.safety,
                          flexShrink: 0,
                          marginTop: 2,
                        }}
                        strokeWidth={2.5}
                      />

                      <Typography
                        component="span"
                        sx={{
                          color: COLORS.muted,
                          textAlign: "left",
                        }}
                      >
                        {f}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Box>
            </Reveal>

            {/* Optional Features */}
            {product.optional.length > 0 && (
              <Reveal delay={0.1}>
                <Box>
                  <Typography
                    component="h2"
                    sx={{
                      fontFamily: "Cabinet Grotesk, system-ui, sans-serif",
                      fontWeight: 900,
                      fontSize: { xs: "1.875rem", md: "2.25rem" },
                      letterSpacing: "-0.03em",
                      color: COLORS.ink,
                      mb: 4,
                      textAlign: "left",
                    }}
                  >
                    Optional Features
                  </Typography>

                  <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0 }}>
                    {product.optional.map((f, i) => (
                      <Box
                        component="li"
                        key={i}
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 2,
                          borderBottom: `1px solid ${COLORS.borderLight}`,
                          pb: 2,
                          mb: 2,
                          "&:last-child": {
                            mb: 0,
                          },
                        }}
                      >
                        <Plus
                          style={{
                            width: 20,
                            height: 20,
                            color: COLORS.steel,
                            flexShrink: 0,
                            marginTop: 2,
                          }}
                          strokeWidth={2.5}
                        />

                        <Typography
                          component="span"
                          sx={{
                            color: COLORS.muted,
                            textAlign: "left",
                          }}
                        >
                          {f}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Reveal>
            )}
          </Box>
        </Container>
      </Box>
      {/* FAQ */}
      <Box
        component="section"
        data-testid="faq-section"
        sx={{
          backgroundColor: COLORS.concrete,
          borderTop: `1px solid ${COLORS.borderLight}`,
          borderBottom: `1px solid ${COLORS.borderLight}`,
          py: { xs: 8, md: 12 },
        }}
      >
        <Container maxWidth="lg">
          <Reveal>
            <Typography
              component="p"
              sx={{
                fontFamily: "monospace",
                fontSize: "0.75rem",
                textTransform: "uppercase",
                letterSpacing: "0.1em",
                color: COLORS.safety,
                mb: 3,
                textAlign: "left",
              }}
            >
              [ FAQ ]
            </Typography>
            <Typography
              component="h2"
              sx={{
                fontFamily: "Cabinet Grotesk, system-ui, sans-serif",
                fontWeight: 900,
                fontSize: { xs: "1.875rem", md: "3rem" },
                letterSpacing: "-0.03em",
                color: COLORS.ink,
                mb: 5,
                textAlign: "left",
              }}
            >
              Common questions
            </Typography>
          </Reveal>
          <Reveal delay={0.1}>
            <ProductFAQ faqs={product.faqs} />
          </Reveal>
        </Container>
      </Box>

      {/* RELATED */}
      <Box component="section" sx={{ py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Reveal>
            <Typography
              component="h2"
              sx={{
                fontFamily: "Cabinet Grotesk, system-ui, sans-serif",
                fontWeight: 900,
                fontSize: { xs: "1.5rem", md: "1.875rem" },
                letterSpacing: "-0.03em",
                color: COLORS.ink,
                mb: 5,
                textAlign: "left",
              }}
            >
              Explore other products
            </Typography>
          </Reveal>
          <Grid container spacing={2}>
            {others.map((p, i) => (
              <Grid item xs={12} md={4} key={p.slug}>
                <Reveal delay={i * 0.08}>
                  <Link
                    component={RouterLink}
                    to={`/products/${p.slug}`}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "between",
                      border: `1px solid ${COLORS.borderMedium}`,
                      p: 3,
                      height: "100%",
                      textDecoration: "none",
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        borderColor: COLORS.safety,
                        backgroundColor: "#ffffff",
                      },
                      "& .arrow-icon": {
                        color: "rgba(26, 26, 26, 0.4)",
                        transition: "all 0.2s ease",
                      },
                      "& .product-title": {
                        color: COLORS.ink,
                        transition: "color 0.2s ease",
                      },
                      "&:hover .arrow-icon": {
                        color: COLORS.safety,
                        transform: "rotate(45deg)",
                      },
                      "&:hover .product-title": {
                        color: COLORS.safety,
                      },
                    }}
                  >
                    <Stack
                      direction="row"
                      useFlexGap
                      sx={{
                        flexGrow: 1,
                        justifyContent: "space-between",
                        alignItems: "center",
                        width: "320px",
                      }}
                    >
                      <Box>
                        <Typography
                          sx={{
                            fontFamily: "monospace",
                            fontSize: "0.75rem",
                            color: COLORS.safety,
                            textAlign: "left",
                          }}
                        >
                          [{p.index}]
                        </Typography>
                        <Typography
                          component="h3"
                          className="product-title"
                          sx={{
                            fontFamily: "display",
                            fontWeight: 700,
                            fontSize: "1.125rem",
                            mt: 0.5,
                            textAlign: "left",
                          }}
                        >
                          {p.short}
                        </Typography>
                      </Box>
                      <ArrowUpRight
                        className="arrow-icon"
                        style={{ width: 20, height: 20 }}
                      />
                    </Stack>
                  </Link>
                </Reveal>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <CTASection />
    </>
  );
}
